import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { dateHeure as dateHeureFr } from "./api";
import { useCharger, api } from "./charger";
import { Chargement, Erreur, Titre } from "./ui";
import type { Moi } from "@/components/atomic-crm/providers/atelier";
import { useMoi } from "./moi";

/* Boîte mail (19/10/2026) : une boîte par personne sur atelieraugmente.fr (server/boite.ts).
   L'admin voit toutes les boîtes ; un commercial, la sienne. Nouveau mail, réponse, brouillons,
   signature, pièces jointes, modèles avec variables remplies selon le prospect. */

const DOSSIERS = [
  { cle: "recus", label: "Reçus" },
  { cle: "envoyes", label: "Envoyés" },
  { cle: "brouillons", label: "Brouillons" },
  { cle: "archives", label: "Archivés" },
];

type Ecrire = { a?: string; cc?: string; sujet?: string; texte?: string; prospectId?: string | null; filDe?: number | null; brouillonId?: number | null; depuis?: string };
type Vue = { type: "liste" } | { type: "lire"; id: number } | { type: "ecrire"; init: Ecrire } | { type: "reglages" };
type InfoBoite = { boite: string; signature: string; boites: string[] };

export function BoitePage() {
  const moi = useMoi() as Moi;
  const admin = moi.role === "admin";
  const [dossier, setDossier] = useState("recus");
  const [filtreBoite, setFiltreBoite] = useState("");
  const [q, setQ] = useState("");
  const [recherche, setRecherche] = useState("");
  const [params] = useSearchParams();
  const [vue, setVue] = useState<Vue>(() => {
    const u = params;
    if (u.get("m")) return { type: "lire", id: Number(u.get("m")) };
    return u.get("a") || u.get("p") ? { type: "ecrire", init: { a: u.get("a") || "", prospectId: u.get("p") || null } } : { type: "liste" };
  });
  const info = useCharger<InfoBoite>("/api/crm/boite/moi");
  const url = `/api/crm/boite?dossier=${dossier}${filtreBoite ? `&boite=${encodeURIComponent(filtreBoite)}` : ""}${recherche ? `&q=${encodeURIComponent(recherche)}` : ""}`;
  const { data, erreur, recharger } = useCharger<{ mails: any[]; nonLus: number }>(url);

  if (info.data && !info.data.boite && !admin) {
    return (
      <div>
        <Titre>Boîte mail</Titre>
        <p className="text-[14px]">Aucune adresse n'est encore attribuée à votre compte. Demandez à l'administrateur de vous en créer une.</p>
      </div>
    );
  }
  const ouvrir = (m: any) => (m.sens === "brouillon"
    ? setVue({ type: "ecrire", init: { a: (m.a || []).join(", "), sujet: m.sujet, texte: undefined, prospectId: m.prospect_id, brouillonId: m.id } })
    : setVue({ type: "lire", id: m.id }));

  return (
    <div>
      <Titre sous={admin ? "Toutes les boîtes d'Atelier Augmenté. Une copie de chaque mail reçu sur alex@ et contact@ part aussi sur votre Gmail." : `Votre boîte : ${info.data?.boite || ""}`}>
        Boîte mail{data?.nonLus ? ` (${data.nonLus} non lu${data.nonLus > 1 ? "s" : ""})` : ""}
      </Titre>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button onClick={() => setVue({ type: "ecrire", init: {} })}>Nouveau mail</Button>
        {DOSSIERS.map((d) => (
          <Button key={d.cle} variant={dossier === d.cle ? "default" : "outline"} onClick={() => { setDossier(d.cle); setVue({ type: "liste" }); }}>{d.label}</Button>
        ))}
        {admin && info.data && (
          <select className="h-9 rounded-md border px-2 text-sm" value={filtreBoite} onChange={(e) => setFiltreBoite(e.target.value)}>
            <option value="">Toutes les boîtes</option>
            {info.data.boites.map((b) => <option key={b} value={b}>{b === "campagne-explee" ? "Réponses à la campagne Explee" : b}</option>)}
          </select>
        )}
        <form className="flex gap-1" onSubmit={(e) => { e.preventDefault(); setRecherche(q.trim()); }}>
          <input className="h-9 w-[220px] rounded-md border px-2 text-sm" placeholder="Rechercher" value={q} onChange={(e) => setQ(e.target.value)} />
        </form>
        <Button variant="outline" className="ml-auto" onClick={() => setVue({ type: "reglages" })}>Modèles et signature</Button>
      </div>
      {erreur && <Erreur message={erreur} />}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[380px_1fr]">
        <ul className="max-h-[75vh] overflow-y-auto border border bg-card">
          {!data && <li className="p-4"><Chargement /></li>}
          {data?.mails.length === 0 && <li className="p-4 text-[14px] text-[#777]">Aucun mail.</li>}
          {data?.mails.map((m) => {
            const gras = m.sens === "recu" && !m.lu;
            const actif = (vue.type === "lire" && vue.id === m.id) || (vue.type === "ecrire" && vue.init.brouillonId === m.id);
            return (
              <li key={m.id}>
                <button className={`block w-full border-b border-[#e5e5e5] p-3 text-left text-[14px] hover:bg-muted ${actif ? "bg-muted" : ""}`} onClick={() => ouvrir(m)}>
                  <div className="flex justify-between gap-2">
                    <span className={gras ? "truncate font-extrabold" : "truncate"}>
                      {m.sens === "recu" ? m.de : `À : ${(m.a || []).join(", ") || "(sans destinataire)"}`}
                    </span>
                    <span className="shrink-0 text-[12px] text-[#777]">{dateHeureFr(m.recu_le)}</span>
                  </div>
                  <div className={gras ? "truncate font-bold" : "truncate"}>
                    {m.sujet || "(sans objet)"}{m.nb_pieces > 0 ? " · pièce jointe" : ""}{m.repondu ? " · répondu" : ""}
                  </div>
                  <div className="truncate text-[12.5px] text-[#666]">
                    {admin && <span className="mr-1 text-orange-700">{(m.boite || "").split("@")[0]}@</span>}
                    {m.prospect_nom ? `${m.prospect_nom} · ` : ""}{m.apercu}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <div>
          {vue.type === "liste" && <p className="text-[14px] text-[#777]">Choisissez un mail, ou écrivez-en un nouveau.</p>}
          {vue.type === "lire" && (
            <Lecture key={vue.id} id={vue.id} apres={recharger}
              repondre={(m) => setVue({ type: "ecrire", init: { a: adresse(m.de), sujet: /^re\s*:/i.test(m.sujet) ? m.sujet : `Re: ${m.sujet}`, filDe: m.id, prospectId: m.prospect_id, depuis: m.boite, texte: m.__texte } })} />
          )}
          {vue.type === "ecrire" && info.data && (
            <Composer key={JSON.stringify(vue.init)} init={vue.init} info={info.data} admin={admin}
              fini={() => { setVue({ type: "liste" }); recharger(); }} />
          )}
          {vue.type === "reglages" && info.data && <Reglages info={info.data} moi={moi} apres={info.recharger} />}
        </div>
      </div>
    </div>
  );
}

const adresse = (v: string) => (String(v || "").match(/<([^>]+)>/)?.[1] || String(v || "")).trim();

function Lecture({ id, apres, repondre }: { id: number; apres: () => void; repondre: (m: any) => void }) {
  const { data, erreur, recharger } = useCharger<{ mail: any; reponses: any[] }>(`/api/crm/boite/${id}`);
  useEffect(() => { if (data) apres(); }, [data?.mail?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (erreur) return <Erreur message={erreur} />;
  if (!data) return <Chargement />;
  const m = data.mail;
  const pieces: any[] = Array.isArray(m.pieces) ? m.pieces : [];
  return (
    <div className="border border bg-card p-4">
      <h2 className="mb-1 text-[18px] font-extrabold">{m.sujet}</h2>
      <p className="text-[13px] text-[#555]">
        De : <strong>{m.de}</strong> · À : {(m.a || []).join(", ")}{m.cc?.length ? ` · Cc : ${m.cc.join(", ")}` : ""} · {dateHeureFr(m.recu_le)}
      </p>
      {m.prospect_id && (
        <p className="text-[13px]">Prospect : {m.contact_id ? <Link to={`/contacts/${m.contact_id}/show`} className="font-bold underline">{m.prospect_nom || "voir la fiche"}</Link> : <span className="font-bold">{m.prospect_nom}</span>}</p>
      )}
      {pieces.length > 0 && (
        <p className="mt-1 text-[13px]">
          Pièces jointes :{" "}
          {pieces.map((p, i) => m.sens === "recu" && p.id
            ? <a key={p.id} className="mr-2 underline" href={`/api/crm/boite/${m.id}/piece/${p.id}`} target="_blank" rel="noreferrer">{p.nom}</a>
            : <span key={i} className="mr-2">{p.nom}</span>)}
        </p>
      )}
      <hr className="my-3 border-[#e5e5e5]" />
      {m.texte ? (
        <pre className="whitespace-pre-wrap font-sans text-[14px] leading-[1.55]">{m.texte}</pre>
      ) : m.html ? (
        /* HTML du mail isolé : ni script, ni formulaire, ni accès à la page. */
        <iframe title="Contenu du mail" sandbox="" srcDoc={m.html} className="h-[480px] w-full border border-[#e5e5e5]" />
      ) : <p className="text-[14px] text-[#777]">(message vide)</p>}

      {data.reponses.map((r) => (
        <div key={r.id} className="mt-4 border-l-4 border-orange-600 pl-3">
          <p className="text-[12px] text-[#777]">Réponse envoyée · {dateHeureFr(r.recu_le)} · depuis {r.de}</p>
          <pre className="whitespace-pre-wrap font-sans text-[14px]">{r.texte}</pre>
        </div>
      ))}

      {m.sens === "recu" && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => repondre(m)}>Répondre</Button>
          <Button variant="outline"  onClick={() => api.patch(`/api/crm/boite/${m.id}`, { archive: !m.archive }).then(() => { recharger(); apres(); })}>
            {m.archive ? "Remettre dans les reçus" : "Archiver"}
          </Button>
          <Button variant="outline" onClick={() => api.patch(`/api/crm/boite/${m.id}`, { lu: false }).then(apres)}>Marquer non lu</Button>
        </div>
      )}
    </div>
  );
}

function lireEnBase64(f: File): Promise<string> {
  return new Promise((ok, ko) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result).split(",")[1] || "");
    r.onerror = () => ko(new Error("Lecture du fichier impossible"));
    r.readAsDataURL(f);
  });
}

function Composer({ init, info, admin, fini }: { init: Ecrire; info: InfoBoite; admin: boolean; fini: () => void }) {
  const [depuis, setDepuis] = useState(init.depuis && info.boites.includes(init.depuis) ? init.depuis : info.boite || info.boites[0] || "");
  const [a, setA] = useState(init.a || "");
  const [cc, setCc] = useState(init.cc || "");
  const [sujet, setSujet] = useState(init.sujet || "");
  const [texte, setTexte] = useState(init.texte || "");
  const [prospectId, setProspectId] = useState<string | null>(init.prospectId || null);
  const [prospectNom, setProspectNom] = useState<string>("");
  const [brouillonId, setBrouillonId] = useState<number | null>(init.brouillonId || null);
  const [pieces, setPieces] = useState<{ nom: string; type: string; base64: string; taille: number }[]>([]);
  const [etat, setEtat] = useState<"repos" | "envoi" | "brouillon">("repos");
  const [message, setMessage] = useState<{ ok: boolean; t: string } | null>(null);
  const modeles = useCharger<{ modeles: any[] }>("/api/crm/boite/modeles");

  /* Brouillon rouvert : on recharge son texte complet. */
  useEffect(() => {
    if (!init.brouillonId) return;
    api.get<{ mail: any }>(`/api/crm/boite/${init.brouillonId}`).then((d) => {
      setTexte(d.mail.texte || ""); setCc((d.mail.cc || []).join(", ")); setSujet(d.mail.sujet || "");
      if (d.mail.boite) setDepuis(d.mail.boite);
    }).catch(() => {});
  }, [init.brouillonId]);
  /* Prospect : depuis la fiche (?p=) ou retrouvé par l'adresse du destinataire. */
  useEffect(() => {
    if (prospectId) {
      api.get<any>(`/api/crm/prospects/${prospectId}`).then((d) => {
        setProspectNom(d?.prospect?.entreprise || "");
        if (!a && d?.prospect?.email) setA(d.prospect.email);
      }).catch(() => {});
      return;
    }
    const premier = a.split(/[,;\s]+/)[0];
    if (!premier || !premier.includes("@")) return;
    const t = setTimeout(() => api.get<{ prospect: any }>(`/api/crm/boite/prospect?email=${encodeURIComponent(premier)}`)
      .then((d) => { if (d.prospect) { setProspectId(d.prospect.id); setProspectNom(d.prospect.entreprise || ""); } }).catch(() => {}), 500);
    return () => clearTimeout(t);
  }, [a, prospectId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function insererModele(id: string) {
    if (!id) return;
    const d = await api.post<{ sujet: string; corps: string }>(`/api/crm/boite/modeles/${id}/remplir`, { prospectId });
    if (!sujet || init.filDe) setSujet(init.filDe ? sujet : d.sujet);
    setTexte((t) => (t.trim() ? `${t.trimEnd()}\n\n${d.corps}` : d.corps));
  }
  async function ajouterPieces(files: FileList | null) {
    if (!files) return;
    const nouvelles = await Promise.all([...files].map(async (f) => ({ nom: f.name, type: f.type || "application/octet-stream", taille: f.size, base64: await lireEnBase64(f) })));
    setPieces((p) => [...p, ...nouvelles].slice(0, 10));
  }
  const total = pieces.reduce((s, p) => s + p.taille, 0);

  async function enregistrer() {
    setEtat("brouillon"); setMessage(null);
    try {
      const d = await api.post<{ id: number }>("/api/crm/boite/brouillons", { id: brouillonId, depuis, a, cc, sujet, texte, prospectId, filDe: init.filDe || null });
      setBrouillonId(d.id); setMessage({ ok: true, t: "Brouillon enregistré." });
    } catch (e: any) { setMessage({ ok: false, t: e.message }); }
    setEtat("repos");
  }
  async function envoyer() {
    if (total > 10 * 1024 * 1024) { setMessage({ ok: false, t: "Pièces jointes trop lourdes (10 Mo au plus)." }); return; }
    setEtat("envoi"); setMessage(null);
    try {
      await api.post("/api/crm/boite/envoyer", { depuis, a, cc, sujet, texte, prospectId, filDe: init.filDe || null, brouillonId, pieces: pieces.map(({ nom, type, base64 }) => ({ nom, type, base64 })) });
      fini();
    } catch (e: any) { setMessage({ ok: false, t: e.message }); setEtat("repos"); }
  }
  const restants = (texte.match(/\[à compléter : [^\]]+\]/g) || []).length;

  return (
    <div className="border border bg-card p-4">
      <h2 className="mb-3 text-[18px] font-extrabold">{init.filDe ? "Répondre" : brouillonId ? "Brouillon" : "Nouveau mail"}</h2>
      <div className="grid grid-cols-[70px_1fr] items-center gap-2 text-[14px]">
        <label>De</label>
        {admin && info.boites.length > 1 && depuis !== "campagne-explee" ? (
          <select className="h-9 rounded-md border px-2 text-sm" value={depuis} onChange={(e) => setDepuis(e.target.value)}>
            {info.boites.filter((b) => b.includes("@")).map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        ) : <span className="font-semibold">{depuis === "campagne-explee" ? "la boîte Explee de la campagne (même fil)" : depuis}</span>}
        <label htmlFor="mail-a">À</label>
        <Input id="mail-a" value={a} onChange={(e) => { setA(e.target.value); if (!init.prospectId) setProspectId(null); }} placeholder="adresse@entreprise.fr (plusieurs : séparées par une virgule)" />
        <label htmlFor="mail-cc">Cc</label>
        <Input id="mail-cc" value={cc} onChange={(e) => setCc(e.target.value)} />
        <label htmlFor="mail-sujet">Objet</label>
        <Input id="mail-sujet" value={sujet} onChange={(e) => setSujet(e.target.value)} />
      </div>
      {prospectId && (
        <p className="mt-2 text-[13px]">Prospect : <span className="font-bold">{prospectNom || "fiche liée"}</span> · les variables des modèles sont remplies avec sa fiche.</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select className="h-9 rounded-md border px-2 text-sm" value="" onChange={(e) => insererModele(e.target.value)}>
          <option value="">Insérer un modèle…</option>
          {modeles.data?.modeles.map((m) => <option key={m.id} value={m.id}>{m.nom}</option>)}
        </select>
        <label className="inline-flex h-9 cursor-pointer items-center rounded-md border px-3 text-sm font-medium">
          Joindre un fichier
          <input type="file" multiple className="hidden" onChange={(e) => { ajouterPieces(e.target.files); e.target.value = ""; }} />
        </label>
      </div>
      <Textarea className="mt-2" rows={14} value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Votre message" />
      {restants > 0 && <p className="text-[13px] text-red-700">{restants} passage{restants > 1 ? "s" : ""} « [à compléter] » à remplir avant l'envoi.</p>}
      {pieces.length > 0 && (
        <ul className="mt-1 text-[13px]">
          {pieces.map((p, i) => (
            <li key={i}>{p.nom} ({Math.ceil(p.taille / 1024)} Ko) <button className="underline" onClick={() => setPieces((x) => x.filter((_, j) => j !== i))}>retirer</button></li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-[12px] text-[#777]">
        Votre signature est ajoutée à l'envoi{info.signature ? ` : « ${info.signature.split("\n")[0]}… »` : ""}{init.filDe ? ", suivie du message d'origine cité" : ""}.
      </p>
      {message && <p className={`mt-2 text-[13px] ${message.ok ? "text-green-800" : "text-red-700"}`}>{message.t}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button disabled={etat !== "repos" || !a.trim() || !sujet.trim() || !texte.trim() || restants > 0} onClick={envoyer}>
          {etat === "envoi" ? "Envoi…" : "Envoyer"}
        </Button>
        <Button variant="outline"  disabled={etat !== "repos"} onClick={enregistrer}>Enregistrer le brouillon</Button>
        {brouillonId && (
          <Button variant="outline"  onClick={() => api.del(`/api/crm/boite/brouillons/${brouillonId}`).then(fini)}>Supprimer le brouillon</Button>
        )}
        <Button variant="outline"  onClick={fini}>Fermer</Button>
      </div>
    </div>
  );
}

function Reglages({ info, moi, apres }: { info: InfoBoite; moi: Moi; apres: () => void }) {
  const [signature, setSignature] = useState(info.signature || "");
  const [ok, setOk] = useState("");
  const { data, recharger } = useCharger<{ modeles: any[] }>("/api/crm/boite/modeles");
  const [edite, setEdite] = useState<any | null>(null);
  return (
    <div className="border border bg-card p-4">
      <h2 className="mb-2 text-[18px] font-extrabold">Ma signature</h2>
      <Textarea rows={4} value={signature} onChange={(e) => setSignature(e.target.value)} />
      <Button className="mt-2" onClick={() => api.put("/api/crm/boite/signature", { signature }).then(() => { setOk("Signature enregistrée."); apres(); })}>Enregistrer la signature</Button>
      {ok && <span className="ml-2 text-[13px] text-green-800">{ok}</span>}

      <h2 className="mb-2 mt-6 text-[18px] font-extrabold">Modèles de mail</h2>
      <p className="mb-2 text-[13px] text-[#555]">
        Variables remplies selon la fiche du prospect : {"{{entreprise}}"}, {"{{lien}}"} (lien du simulateur à son nom), {"{{objet}}"}, {"{{question}}"}, {"{{exemples}}"} (selon son métier).
        Les variantes {"{a|b}"} des modèles Explee gardent la première formulation.
      </p>
      <ul className="mb-3 divide-y divide-[#e5e5e5] border border-[#e5e5e5]">
        {data?.modeles.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-2 p-2 text-[14px]">
            <span>{m.nom}</span>
            {(moi.role === "admin" || m.auteur_id === moi.id) && <button className="underline" onClick={() => setEdite(m)}>modifier</button>}
          </li>
        ))}
      </ul>
      <Button variant="outline"  onClick={() => setEdite({ nom: "", sujet: "", corps: "" })}>Nouveau modèle</Button>
      {edite && (
        <div className="mt-3 border-l-4 border-orange-600 pl-3">
          <Input className="mb-2" placeholder="Nom du modèle" value={edite.nom} onChange={(e) => setEdite({ ...edite, nom: e.target.value })} />
          <Input className="mb-2" placeholder="Objet" value={edite.sujet} onChange={(e) => setEdite({ ...edite, sujet: e.target.value })} />
          <Textarea className="mb-2" rows={12} value={edite.corps} onChange={(e) => setEdite({ ...edite, corps: e.target.value })} />
          <div className="flex flex-wrap gap-2">
            <Button onClick={async () => {
              if (edite.id) await api.patch(`/api/crm/boite/modeles/${edite.id}`, edite); else await api.post("/api/crm/boite/modeles", edite);
              setEdite(null); recharger();
            }}>Enregistrer</Button>
            {edite.id && <Button variant="outline"  onClick={() => api.del(`/api/crm/boite/modeles/${edite.id}`).then(() => { setEdite(null); recharger(); })}>Supprimer</Button>}
            <Button variant="outline"  onClick={() => setEdite(null)}>Annuler</Button>
          </div>
        </div>
      )}
    </div>
  );
}
